import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "environments/environment";
import { HttpClient } from "@angular/common/http";

import { LicenseApiResponse } from "app/shared/models/license-api-response.interface";
import { Account } from "app/shared/models/account";
import { License, LicenseType } from "app/shared/models/license.interface";
import { filter, map, switchMap } from "rxjs/operators";
import { AccountService } from "app/core/services/account.service";

@Injectable()
export class LicenseService {
  private isUserHasConferenceServiceLicenseSource: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private isUserHasFaxServiceLicenseSource: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public isUserHasConferenceServiceLicense$: Observable<boolean> =
    this.isUserHasConferenceServiceLicenseSource.asObservable();
  public isUserHasFaxServiceLicense$: Observable<boolean> =
    this.isUserHasFaxServiceLicenseSource.asObservable();

  constructor(
    private http: HttpClient,
    private accountService: AccountService,
  ) {
    this.accountService.selectedAccount$
      .pipe(
        filter((account: Account | null) => !!account),
        switchMap((account: Account | null) => this.getLicenses(account!)),
      )
      .subscribe((licenses: License[]) => {
        if (!!licenses.length) {
          this.isUserHasConferenceServiceLicenseSource.next(
            licenses?.find(
              (item: License) => item.product_code === "conferencePerService",
            )?.booked !== 0,
          );
          this.isUserHasFaxServiceLicenseSource.next(
            licenses?.find(
              (item: License) => item.product_code === "faxPerExtension",
            )?.booked !== 0,
          );
        }
      });
  }

  public getLicenses(account: Account): Observable<License[]> {
    return this.http.get<License[]>(
      `${this.apiEndpointAdminAccounts}/${account.accountType}:${account.accountId}/products`,
    );
  }

  public getLicensesUsage(
    accountTypeAndId: string,
    licenseType: LicenseType,
  ): Observable<LicenseApiResponse> {
    const options: any = { observe: "response" };
    const url: string = this.getLicenseUsageUrl(accountTypeAndId, licenseType);

    return this.http.get(url, options).pipe(
      // Using any type because otherwise it was trying to use ArrayBuffer. Further investigation needed.
      map(({ body, status }: any) => ({ body, status })),
    );
  }

  public getUserLicenseState(
    accountId: string,
    userId: number,
    licenseType: string,
  ): Observable<object> {
    return this.http.get(
      `${this.apiEndpointAdminAccounts}/${accountId}/users/${userId}/licences/${licenseType}`,
    );
  }

  public grantLicenseToUser(
    accountId: string,
    userId: number,
    licenseType: string,
  ): Observable<object> {
    return this.http.post(
      `${this.apiEndpointAdminAccounts}/${accountId}/users/${userId}/licences/${licenseType}`,
      {},
    );
  }

  public revokeLicenseFromUser(
    accountId: string,
    userId: number,
    licenseType: string,
  ): Observable<object> {
    return this.http.delete(
      `${this.apiEndpointAdminAccounts}/${accountId}/users/${userId}/licences/${licenseType}`,
    );
  }

  private getLicenseUsageUrl(
    accountTypeAndId: string,
    licenseType: LicenseType,
  ): string {
    return `${this.apiEndpointAdminAccounts}/${accountTypeAndId}/products/${licenseType}`;
  }

  private get apiEndpointAdminAccounts(): string {
    return `${environment.API_URL}/admin/accounts`;
  }
}
